import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, timeAgo, formatApiError } from "../lib/api";
import Pager from "./Pager";

export function TalentDirectory() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState("");

  const load = (p = page, query = q) =>
    api.get("/admin/talent/candidates", { params: { page: p, q: query } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load candidates"));

  useEffect(() => { load(1, ""); }, []);

  const search = () => { setPage(1); load(1, q); };

  const toggle = async (c) => {
    setBusy(c.id);
    try {
      await api.patch(`/admin/talent/candidates/${c.id}`, { discoverable: !c.discoverable });
      toast.success(c.discoverable ? `${c.name} hidden from the talent pool` : `${c.name} is discoverable again`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(""); }
  };

  return (
    <div className="mt-6 border border-zinc-200 rounded-md p-5 bg-white" data-testid="talent-directory">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <p className="font-display font-bold text-zinc-950">Candidate directory</p>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-lg">
            Every candidate account. Toggle <em>discoverable</em> to add or remove someone from employer talent searches, hiding also cancels their pending contact requests. Changes are consent-logged and audited.
          </p>
        </div>
        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search name, email, headline…" data-testid="talent-dir-search"
            className="h-9 w-64 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
          <button onClick={search} data-testid="talent-dir-search-btn"
            className="h-9 px-3 border border-zinc-300 rounded-md text-zinc-600 hover:border-zinc-900 hover:text-zinc-900 transition-colors duration-200" aria-label="Search">
            <Search size={15} />
          </button>
        </div>
      </div>
      {!data ? (
        <div className="h-32 rounded-md bg-zinc-50 animate-pulse" />
      ) : data.items.length === 0 ? (
        <p className="text-sm text-zinc-400" data-testid="talent-dir-empty">No candidates match.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                  <th className="py-2 pr-3 font-semibold">Candidate</th>
                  <th className="py-2 pr-3 font-semibold">Headline</th>
                  <th className="py-2 pr-3 font-semibold">Location</th>
                  <th className="py-2 pr-3 font-semibold">Skills</th>
                  <th className="py-2 pr-3 font-semibold">Joined</th>
                  <th className="py-2 font-semibold text-right">In talent pool</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-50" data-testid={`talent-dir-row-${c.id}`}>
                    <td className="py-2.5 pr-3">
                      <p className="font-semibold text-zinc-900">{c.name}</p>
                      <p className="text-xs text-zinc-400">{c.email}</p>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-zinc-600 max-w-44 truncate" title={c.headline}>{c.headline || "—"}</td>
                    <td className="py-2.5 pr-3 text-xs text-zinc-600">{c.location || "—"}</td>
                    <td className="py-2.5 pr-3 text-xs text-zinc-600">{c.skills || "—"}</td>
                    <td className="py-2.5 pr-3 text-xs text-zinc-500 whitespace-nowrap">{timeAgo(c.joined)}</td>
                    <td className="py-2.5 text-right">
                      <button onClick={() => toggle(c)} disabled={busy === c.id} data-testid={`talent-dir-toggle-${c.id}`}
                        title={c.discoverable ? "Hide from employer talent searches" : "Make visible in employer talent searches"}
                        className={`h-7 px-3 text-[11px] font-semibold rounded-full transition-colors duration-200 disabled:opacity-50 ${c.discoverable ? "bg-green-50 text-green-700 hover:bg-green-100" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}>
                        {busy === c.id ? "…" : c.discoverable ? "Discoverable" : "Hidden"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} pages={data.pages} onPage={(p) => { setPage(p); load(p); }} />
        </>
      )}
    </div>
  );
}
